import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Shield, Check, X } from "lucide-react";
import { usePlanosAtivos } from "@/hooks/usePlanos";
import { useFormasPagamentoAtivas } from "@/hooks/useFormasPagamento";
import { isFormaPagamentoCartao, numeroParcelasAoSelecionarForma } from "@/lib/orcamentoFormaPagamento";
import { toast } from "sonner";

interface SelecionarPlanoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (planoData: {
    tipo_plano: string;
    vigencia_anos: number;
    quantidade_placas: number;
    valor_total: number;
    valor_tabela: number;
    desconto_valor: number;
    forma_pagamento_id: string;
    parcelas_cartao: number;
  }) => void;
  valorBase: number;
  quantidadePainels?: number;
}

function multiplicadorVigencia(anos: number): { mult: number; descontoText: string } {
  switch (anos) {
    case 1:
      return { mult: 1, descontoText: "" };
    case 2:
      return { mult: 1.8, descontoText: " (10% desconto)" };
    case 3:
      return { mult: 2.5, descontoText: " (17% desconto)" };
    case 5:
      return { mult: 3.8, descontoText: " (24% desconto)" };
    case 10:
      return { mult: 6.5, descontoText: " (35% desconto)" };
    default:
      return { mult: anos, descontoText: "" };
  }
}

function formatBRL(n: number) {
  return `R$ ${n.toFixed(2).replace(".", ",")}`;
}

export function SelecionarPlanoDialog({
  open,
  onOpenChange,
  onConfirm,
  valorBase,
  quantidadePainels = 0,
}: SelecionarPlanoDialogProps) {
  const { data: planosAtivos = [] } = usePlanosAtivos();
  const { data: formasPagamento = [] } = useFormasPagamentoAtivas();

  const [planoSelecionado, setPlanoSelecionado] = useState("");
  const [vigenciaAnos, setVigenciaAnos] = useState(1);
  const [quantidadePlacas, setQuantidadePlacas] = useState(quantidadePainels);
  const [valorTabela, setValorTabela] = useState(valorBase);
  const [valorFinal, setValorFinal] = useState(valorBase);
  const [formaPagamentoId, setFormaPagamentoId] = useState("");
  const [parcelasCartao, setParcelasCartao] = useState(1);

  useEffect(() => {
    if (open && quantidadePainels > 0) {
      setQuantidadePlacas(quantidadePainels);
    }
  }, [open, quantidadePainels]);

  // Recalcular valor de tabela quando mudar plano, quantidade ou vigência
  useEffect(() => {
    if (planoSelecionado && quantidadePlacas > 0) {
      const plano = planosAtivos.find((p) => p.id === planoSelecionado);
      if (plano) {
        const valorBasePorPainel = quantidadePlacas * plano.valor_por_painel;
        const { mult } = multiplicadorVigencia(vigenciaAnos);
        const novoValor = valorBasePorPainel * mult;
        setValorTabela(novoValor);
        setValorFinal(novoValor);
        return;
      }
    }
    setValorTabela(valorBase);
    setValorFinal(valorBase);
  }, [planoSelecionado, quantidadePlacas, vigenciaAnos, planosAtivos, valorBase]);

  const descontoValor = Math.max(0, Math.round((valorTabela - valorFinal) * 100) / 100);

  const handleConfirm = () => {
    if (!planoSelecionado) {
      toast.error("Selecione um plano");
      return;
    }

    if (quantidadePlacas <= 0) {
      toast.error("Defina a quantidade de painéis");
      return;
    }

    if (!formaPagamentoId) {
      toast.error("Selecione a forma de pagamento");
      return;
    }

    if (!valorFinal || valorFinal <= 0) {
      toast.error("Informe o valor final da proposta");
      return;
    }

    if (valorFinal > valorTabela + 0.009) {
      toast.error("O valor final não pode ser maior que o valor de tabela");
      return;
    }

    const parcelas = numeroParcelasAoSelecionarForma(
      formaPagamentoId,
      formasPagamento,
      parcelasCartao
    );

    onConfirm({
      tipo_plano: planoSelecionado,
      vigencia_anos: vigenciaAnos,
      quantidade_placas: quantidadePlacas,
      valor_total: valorFinal,
      valor_tabela: valorTabela,
      desconto_valor: descontoValor,
      forma_pagamento_id: formaPagamentoId,
      parcelas_cartao: parcelas,
    });

    onOpenChange(false);
  };

  const getPlanoNome = (tipo: string) => {
    switch (tipo) {
      case "basico":
        return "Básico";
      case "elevo":
        return "Elevo";
      case "renovacao-elevo":
        return "Renovação Elevo";
      case "personalizado":
        return "Personalizado";
      default:
        return tipo;
    }
  };

  const getPlanoColor = (tipo: string) => {
    switch (tipo) {
      case "elevo":
        return "bg-blue-100 text-blue-800";
      case "renovacao-elevo":
        return "bg-purple-100 text-purple-800";
      case "personalizado":
        return "bg-orange-100 text-orange-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-gray-900">
            <Shield className="h-5 w-5 text-green-600" />
            Selecionar Plano de Seguro
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div>
            <Label htmlFor="plano">Tipo de Plano</Label>
            <Select value={planoSelecionado} onValueChange={setPlanoSelecionado}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione um plano de seguro" />
              </SelectTrigger>
              <SelectContent>
                {planosAtivos.map((plano) => (
                  <SelectItem key={plano.id} value={plano.id}>
                    <div className="flex items-center gap-2">
                      <Badge className={getPlanoColor(plano.nome)}>
                        {getPlanoNome(plano.nome)}
                      </Badge>
                      <span>{plano.nome}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {planoSelecionado && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Detalhes do Plano</CardTitle>
              </CardHeader>
              <CardContent>
                {(() => {
                  const plano = planosAtivos.find((p) => p.id === planoSelecionado);
                  if (!plano) return null;

                  return (
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Nome:</span>
                        <span className="font-medium">{plano.nome}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Valor por Painel:</span>
                        <span className="font-medium">
                          R$ {plano.valor_por_painel?.toFixed(2).replace(".", ",")}
                        </span>
                      </div>
                      {plano.descricao && (
                        <div className="text-sm text-gray-600 mt-2">{plano.descricao}</div>
                      )}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="forma_pagamento">Forma de Pagamento *</Label>
              <Select
                value={formaPagamentoId}
                onValueChange={(value) => {
                  setFormaPagamentoId(value);
                  const forma = formasPagamento.find((f) => f.id.toString() === value);
                  if (forma?.tipo === "cartao") {
                    setParcelasCartao(Math.min(parcelasCartao, Math.max(1, Number(forma.parcelas_max) || 6)));
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a forma de pagamento" />
                </SelectTrigger>
                <SelectContent>
                  {formasPagamento.map((forma) => (
                    <SelectItem key={forma.id} value={forma.id.toString()}>
                      {forma.nome}
                      {Number(forma.parcelas_max) > 1 ? ` (${forma.parcelas_max}x)` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {isFormaPagamentoCartao(formaPagamentoId, formasPagamento) && (
              <div>
                <Label htmlFor="parcelas_cartao">Parcelas no cartão</Label>
                <Select
                  value={parcelasCartao.toString()}
                  onValueChange={(v) => setParcelasCartao(parseInt(v, 10) || 1)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => (
                      <SelectItem key={n} value={n.toString()}>
                        {n}x
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="quantidade_placas">Quantidade de Painéis</Label>
              <Input
                id="quantidade_placas"
                type="number"
                min="1"
                value={quantidadePlacas}
                onChange={(e) => setQuantidadePlacas(parseInt(e.target.value) || 0)}
                placeholder="Ex: 10"
              />
            </div>

            <div>
              <Label htmlFor="vigencia_anos">Vigência (anos)</Label>
              <Select
                value={vigenciaAnos.toString()}
                onValueChange={(value) => setVigenciaAnos(parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 ano</SelectItem>
                  <SelectItem value="2">2 anos</SelectItem>
                  <SelectItem value="3">3 anos</SelectItem>
                  <SelectItem value="5">5 anos</SelectItem>
                  <SelectItem value="10">10 anos</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Card className="bg-green-50 border border-green-200">
            <CardHeader>
              <CardTitle className="text-sm text-gray-900">Resumo do Orçamento</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Quantidade de Painéis:</span>
                  <span className="font-medium text-gray-900">{quantidadePlacas} painéis</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Vigência:</span>
                  <span className="font-medium text-gray-900">
                    {vigenciaAnos} ano{vigenciaAnos > 1 ? "s" : ""}
                  </span>
                </div>
                {(() => {
                  const plano = planosAtivos.find((p) => p.id === planoSelecionado);
                  if (plano && quantidadePlacas > 0) {
                    const valorBasePorPainel = quantidadePlacas * plano.valor_por_painel;
                    const { mult, descontoText } = multiplicadorVigencia(vigenciaAnos);

                    return (
                      <>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Valor Base:</span>
                          <span className="font-medium text-gray-900">
                            {formatBRL(valorBasePorPainel)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">
                            Multiplicador ({vigenciaAnos} ano{vigenciaAnos > 1 ? "s" : ""}):
                          </span>
                          <span className="font-medium text-gray-900">
                            {mult}x{descontoText}
                          </span>
                        </div>
                      </>
                    );
                  }
                  return null;
                })()}
                <div className="flex justify-between border-t border-green-200 pt-2">
                  <span className="text-sm text-gray-700 font-medium">Valor de tabela:</span>
                  <span className="font-bold text-gray-900">{formatBRL(valorTabela)}</span>
                </div>

                <div className="pt-2 space-y-2">
                  <Label htmlFor="valor_final_proposta" className="text-gray-900">
                    Valor final da proposta (R$)
                  </Label>
                  <Input
                    id="valor_final_proposta"
                    type="number"
                    min={0}
                    step="0.01"
                    value={Number.isFinite(valorFinal) ? valorFinal : ""}
                    onChange={(e) => setValorFinal(parseFloat(e.target.value) || 0)}
                    className="bg-white"
                  />
                  <p className="text-xs text-gray-600">
                    Desconto comercial:{" "}
                    <strong className="text-green-700">{formatBRL(descontoValor)}</strong>
                    {descontoValor > 0 ? " (registrado no seu nome)" : ""}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            <X className="h-4 w-4 mr-2" />
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            <Check className="h-4 w-4 mr-2" />
            Confirmar Plano
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
